/**
 * Các bảng "theo loại việc" máy chủ đã tính sẵn: hậu kỳ, CSKH, lễ tân, và
 * danh sách bán thêm.
 *
 * TIỀN của hai phòng Hậu kỳ và CSKH vẫn tính từ số TỔNG cả tháng trưởng phòng
 * chốt (một dòng mỗi người mỗi tháng). Sổ từng job chạy SONG SONG bên cạnh:
 * nó liệt kê từng đơn để nhân viên đối chiếu, nhưng KHÔNG được dùng để tính
 * lại tiền — hai nguồn cùng nói về tiền là hai nguồn sẽ lệch nhau.
 *
 * Chưa có sổ thì mục này quay về đúng mức nói thật cũ ("500 × 0,8 thạch
 * lương thảo (= 8.000đ)"), chứ không bịa ra danh sách.
 */

import { keepPayUnitTogether } from "@/features/salary-rules-v2/pay-unit";
import type { PayrollRecord } from "../../lib";
import { vnd, upsellCommissionLabel } from "../../constants";
import type { WorkLedgerRow } from "../../workLedgerApi";
import { priceTierText, priceTiers } from "../../salaryDetail";
import { AmountRow, AmountTable, DetailSection, LineTable } from "./primitives";
import { LedgerNote, WorkLedgerLines } from "./WorkLedgerLines";
import { isCrewDept } from "./deptSections";

/** Lọc sổ theo phòng — sổ trả về cả hai phòng của người kiêm nhiệm. */
function linesOfDept(ledger: WorkLedgerRow[] | undefined, dept: string) {
  return (ledger ?? []).filter((r) => r.dept === dept);
}

export function RetouchDetail({
  record,
  ledger,
}: {
  record: PayrollRecord;
  ledger?: WorkLedgerRow[] | undefined;
}) {
  const b = record.retouchBreakdown ?? {};
  const lines = linesOfDept(ledger, "retouch");

  const rows: { label: string; count: number; pay: number }[] = [
    { label: "Ảnh sửa", count: b.photoCount ?? 0, pay: b.photoPay ?? 0 },
    { label: "Thiết kế", count: b.designCount ?? 0, pay: b.designPay ?? 0 },
    { label: "Album", count: b.albumCount ?? 0, pay: b.albumPay ?? 0 },
    { label: "Ghép ảnh", count: b.compositeCount ?? 0, pay: b.compositePay ?? 0 },
    {
      label: "Ảnh sửa từ gói trang phục",
      count: b.costumeEditPhotos ?? 0,
      pay: b.costumeEditPay ?? 0,
    },
    // Album ngoài đơn (QĐ 0309): ảnh giám đốc đã hậu kiểm, cùng đơn giá ảnh sửa.
    { label: "Album ngoài đơn", count: b.looseAlbumPhotos ?? 0, pay: b.looseAlbumPay ?? 0 },
  ].filter((r) => r.count > 0 || r.pay > 0);

  if (!rows.length && !lines.length) return null;
  const total = rows.reduce((sum, r) => sum + r.pay, 0);

  const ledgerPhotos = lines.reduce((sum, r) => sum + (r.qty ?? 0), 0);
  const typedPhotos = b.photoCount ?? 0;

  return (
    <DetailSection
      testId="detail-retouch"
      icon="🖌️"
      title="Hậu kỳ"
      total={total}
      note={
        <LedgerNote
          ledgerCount={lines.length}
          typedLabel={`${typedPhotos} ảnh`}
          mismatch={ledgerPhotos !== typedPhotos}
        />
      }
    >
      <AmountTable>
        {rows.map((r) => {
          // Tiền không chia hết cho số lượng thì KHÔNG ghi đơn giá: làm tròn
          // là in ra "3 × 8.333đ" cạnh 25.000đ, phép nhân không ra đúng tiền.
          const tier = priceTiers([{ qty: r.count, pay: r.pay }])?.[0];
          return (
            <AmountRow
              key={r.label}
              testId={`retouch-${r.label}`}
              label={r.label}
              hint={tier ? keepPayUnitTogether(priceTierText(tier)) : `Số lượng: ${r.count}`}
              value={r.pay}
            />
          );
        })}
      </AmountTable>

      {lines.length ? (
        <div data-testid="retouch-ledger" className="mt-3 border-t pt-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Sổ từng đơn — {lines.length} đơn, {ledgerPhotos} ảnh
          </div>
          <WorkLedgerLines rows={lines} showMoney={false} />
        </div>
      ) : null}
    </DetailSection>
  );
}

export function CskhDetail({
  record,
  ledger,
}: {
  record: PayrollRecord;
  ledger?: WorkLedgerRow[] | undefined;
}) {
  const b = record.cskhBreakdown;
  if (!b) return null;

  const rows = [
    {
      label: "Ảnh mua thêm",
      hint: `Doanh thu ${vnd(b.extraPhotoRevenue ?? 0)}`,
      value: b.extraPhotoCommission ?? 0,
    },
    {
      label: "In ảnh",
      hint: `Doanh thu ${vnd(b.printRevenue ?? 0)}`,
      value: b.printCommission ?? 0,
    },
  ].filter((r) => r.value !== 0);

  const prev = b.prevMonthAdjustment ?? 0;
  // Tháng máy (QĐ 0319): người chỉ có khoản đang chờ khách trả đủ vẫn thấy
  // danh sách — biết tiền của mình đang ở đâu.
  const items = b.machineRevenue ? (record.cskhItems ?? []) : [];
  if (!rows.length && prev === 0 && !items.length) return null;
  const total = rows.reduce((sum, r) => sum + r.value, 0) + prev;

  const lines = linesOfDept(ledger, "cskh");
  const ledgerRevenue = lines.reduce((sum, r) => sum + (r.amount ?? 0), 0);
  const typedRevenue = (b.extraPhotoRevenue ?? 0) + (b.printRevenue ?? 0);

  return (
    <DetailSection
      testId="detail-cskh"
      icon="📞"
      title="CSKH"
      total={total}
      note={
        // Tháng máy: số tiền tính thẳng từ từng khoản bên dưới, không còn số
        // chốt tay nào để đối chiếu với sổ.
        b.machineRevenue ? undefined : (
          <LedgerNote
            ledgerCount={lines.length}
            typedLabel={vnd(typedRevenue)}
            mismatch={ledgerRevenue !== typedRevenue}
          />
        )
      }
    >
      <AmountTable>
        {rows.map((r) => (
          <AmountRow key={r.label} label={r.label} hint={r.hint} value={r.value} />
        ))}
        {prev !== 0 ? (
          <AmountRow
            label="Điều chỉnh tháng trước"
            hint="Khoản cộng/trừ bù cho tháng trước"
            value={prev}
          />
        ) : null}
      </AmountTable>

      {!b.machineRevenue && lines.length ? (
        <div data-testid="cskh-ledger" className="mt-3 border-t pt-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Sổ từng đơn — {lines.length} đơn, doanh thu {vnd(ledgerRevenue)}
          </div>
          <WorkLedgerLines rows={lines} showMoney />
        </div>
      ) : null}

      {items.length ? (
        <div data-testid="cskh-items" className="mt-3 border-t pt-2">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Từng khoản — chỉ khoản khách đã trả đủ mới vào doanh thu
          </div>
          <LineTable
            columns={[
              { key: "order", label: "Đơn" },
              { key: "kind", label: "Loại" },
              { key: "state", label: "Trạng thái" },
              { key: "amount", label: "Doanh thu", className: "text-right" },
            ]}
          >
            {[...items]
              .sort((a, z) => Number(z.matured) - Number(a.matured))
              .map((it, i) => (
                <tr
                  key={`${it.orderId}-${it.kind}-${i}`}
                  className={it.matured ? undefined : "opacity-70"}
                >
                  <td className="px-1 py-1.5 md:px-2">{it.orderCode || it.customer || "—"}</td>
                  <td className="px-1 py-1.5 md:px-2">
                    {it.kind === "print" ? "In ảnh" : "Ảnh mua thêm"}
                  </td>
                  <td className="px-1 py-1.5 text-xs md:px-2">
                    {it.matured ? "Đã tính" : "Chờ khách trả đủ"}
                  </td>
                  <td className="px-1 py-1.5 text-right tabular-nums md:px-2">{vnd(it.amount)}</td>
                </tr>
              ))}
          </LineTable>
        </div>
      ) : null}
    </DetailSection>
  );
}

export function CrewHoursDetail({ record }: { record: PayrollRecord }) {
  if (!isCrewDept(record.department)) return null;
  const hours = record.hoursWorked ?? 0;
  const overtime = record.overtimeHours ?? 0;
  const overtimePay = record.overtimePay ?? 0;
  if (!hours && !overtime) return null;

  return (
    <DetailSection
      testId="detail-crew-hours"
      icon="⏱"
      title="Giờ có mặt"
      {...(overtimePay ? { total: overtimePay } : {})}
    >
      <AmountTable>
        <AmountRow
          label="Giờ có mặt trong tháng"
          hint="Cộng từ các lần Check-in / Ra ca"
          value={`${hours} giờ`}
        />
        {overtime > 0 ? (
          <AmountRow
            label="Giờ tăng ca"
            hint="Phần vượt chuẩn tháng, tính theo hệ số tăng ca của vai trên đơn giá giờ theo lương cứng"
            value={`${overtime} giờ`}
          />
        ) : null}
        {overtimePay > 0 ? <AmountRow label="Tiền tăng ca" value={overtimePay} /> : null}
      </AmountTable>
    </DetailSection>
  );
}

/**
 * Bán thêm — từng khoản, tách rạch ròi ĐÃ CHÍN với CHỜ THU ĐỦ.
 *
 * Đây là câu hỏi số một của cơ chế này: bán rồi mà lương chưa cộng. Dòng chờ
 * hiện mờ và ghi thẳng lý do, thay vì biến mất khiến người bán tưởng hệ thống
 * nuốt mất.
 */
export function UpsellItemsDetail({
  record,
  upsellCommissionRate,
}: {
  record: PayrollRecord;
  /** Tỷ lệ hoa hồng bán thêm hiệu lực của tháng — từ response `upsell.commissionRate`. */
  upsellCommissionRate?: number | undefined;
}) {
  const items = record.upsellItems ?? [];
  if (!items.length) return null;

  const matured = items.filter((i) => i.matured);
  const pending = items.filter((i) => !i.matured);
  const commissionLabel =
    upsellCommissionRate != null ? ` ${upsellCommissionLabel(upsellCommissionRate)}` : "";

  return (
    <DetailSection
      testId="detail-upsell-items"
      icon="🎁"
      title="Bán thêm"
      countLabel={`${items.length} khoản`}
      {...(record.upsellCommission ? { total: record.upsellCommission } : {})}
      note={
        <>
          Hoa hồng{commissionLabel} chỉ tính trên khoản <b>đã chín</b>{" "}
          — tức đơn khách đã thanh toán đủ 100%.
          {pending.length > 0
            ? ` Đang có ${pending.length} khoản chờ khách thanh toán đủ.`
            : ""}
        </>
      }
    >
      <LineTable
        columns={[
          { key: "customer", label: "Khách" },
          { key: "label", label: "Nội dung", className: "hidden md:table-cell" },
          { key: "state", label: "Trạng thái" },
          { key: "amount", label: "Doanh số", className: "text-right" },
        ]}
      >
        {[...matured, ...pending].map((item, i) => (
          <tr key={`${item.orderId}-${i}`} className={item.matured ? undefined : "opacity-70"}>
            <td className="px-1 py-1.5 md:px-2">
              {item.customer || "—"}
              {item.label ? (
                <span className="block text-xs text-muted-foreground md:hidden">
                  {item.label}
                </span>
              ) : null}
            </td>
            <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
              {item.label || "—"}
            </td>
            <td className="px-1 py-1.5 text-xs md:px-2">
              {item.matured ? "Đã chín" : "Chờ thu đủ"}
            </td>
            <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
              {vnd(item.amount)}
            </td>
          </tr>
        ))}
      </LineTable>
    </DetailSection>
  );
}
